import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, ImageIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface LocationImageUploadProps {
  /** Current image URL (for preview) */
  imageUrl?: string | null;
  /** Callback when a file is selected for upload */
  onUpload: (file: File) => void;
  /** Callback when delete is clicked */
  onDelete: () => void;
  /** Whether upload/delete operations are in progress */
  isLoading?: boolean;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * LocationImageUpload - Image upload component for location form
 * Displays current image with option to upload new or delete existing
 */
export function LocationImageUpload({
  imageUrl,
  onUpload,
  onDelete,
  isLoading = false,
  disabled = false,
  className,
}: LocationImageUploadProps) {
  const { t } = useTranslation('locations');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [dragActive, setDragActive] = React.useState(false);

  // Reset preview when imageUrl changes externally and cleanup blob URLs
  React.useEffect(() => {
    // When imageUrl changes, clear the local preview
    setPreviewUrl(null);
  }, [imageUrl]);

  // Cleanup blob URL when component unmounts or previewUrl changes
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      return;
    }

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // Trigger upload callback
    onUpload(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Revoke the blob URL before clearing to prevent memory leak
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    onDelete();
  };

  const displayUrl = previewUrl || imageUrl;
  const hasImage = Boolean(displayUrl);

  return (
    <div className={cn('relative', className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleInputChange}
        disabled={disabled}
      />

      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative border-2 border-dashed rounded-lg transition-colors cursor-pointer',
          'flex items-center justify-center',
          hasImage ? 'border-transparent' : 'border-muted-foreground/25 hover:border-muted-foreground/50',
          dragActive && 'border-primary bg-primary/5',
          disabled && 'opacity-50 cursor-not-allowed',
          'min-h-[150px] overflow-hidden'
        )}
      >
        {isLoading ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="text-sm">{t('form.imageUploading')}</span>
          </div>
        ) : hasImage ? (
          <>
            <img
              src={displayUrl!}
              alt={t('form.imagePreview')}
              className="w-full h-full object-cover max-h-[200px]"
            />
            {/* Overlay with delete button */}
            <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleClick}
                disabled={disabled}
              >
                <Upload className="h-4 w-4 mr-1" />
                {t('form.changeImage')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={disabled}
              >
                <X className="h-4 w-4 mr-1" />
                {t('form.removeImage')}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground p-4">
            <ImageIcon className="h-10 w-10" />
            <span className="text-sm text-center">
              {t('form.imagePlaceholder')}
            </span>
            <span className="text-xs text-muted-foreground/70">
              {t('form.imageDragDrop')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default LocationImageUpload;
